import React, { useEffect, useState } from "react";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

import { FileText, Shield } from "lucide-react";
import { Button } from "../ui/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("terms"); // default tab

  useEffect(() => {
    if (location.state === "policy") setActiveTab("privacy");
    if (location.state === "term") setActiveTab("terms");
  }, [location.state]);

  return (
    <div className="p-4 mx-auto max-w-5xl">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Tabs header */}
        <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
          <TabsList className="flex gap-4 bg-transparent h-auto p-0 overflow-x-auto no-scrollbar">
            <TabsTrigger value="terms" className="px-4 py-2">
              <FileText className="mr-2 h-4 w-4" />
              Terms of Service
            </TabsTrigger>

            <TabsTrigger value="privacy" className="px-4 py-2">
              <Shield className="mr-2 h-4 w-4" />
              Privacy Policy
            </TabsTrigger>
          </TabsList>
        </div>

        {/* TERMS TAB */}
        <TabsContent value="terms" className="mt-4">
          <Card className="rounded-lg shadow-md">
            <CardHeader className="p-6 border-b border-gray-200 dark:border-gray-800">
              <CardTitle className="text-lg font-semibold">
                Terms of Service
              </CardTitle>

              <CardDescription className="mt-1 text-sm">
                Please read these Terms carefully before using the Zenith HRMS &
                Payroll platform.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 space-y-6 text-sm leading-relaxed max-h-[70vh] overflow-y-auto h-[60vh]">
              <section>
                <h3 className="font-semibold mb-2">
                  1. CONTRACTUAL RELATIONSHIP
                </h3>

                <p className="mb-3 leading-relaxed text-gray-700 dark:text-gray-300">
                  These Terms of Service (“Terms”) constitute a legally binding
                  agreement between Element Tree (“Company”, “we”, “us”, or
                  “our”), having its registered office at D-601, Raikar
                  Chambers, Off K.D. Marg, opp Neelkanth Gardens, Govandi
                  (East), Mumbai – 400 088, Maharashtra, India, and the entity
                  or individual (“Client”, “Subscriber”, “you”) that registers
                  for or uses the Zenith HRMS and Payroll platform (“Service” or
                  “Platform”). By creating an account or using the Service, you
                  acknowledge that you have read, understood, and agree to be
                  bound by these Terms. If you are entering into this agreement
                  on behalf of an organization, you represent that you have the
                  authority to bind such entity. This agreement is entered into
                  electronically and does not require physical signatures.
                </p>

                <h3 className="font-semibold mb-2">2. DEFINITIONS</h3>

                <div className="space-y-2 mb-3 text-gray-700 dark:text-gray-300">
                  <p>
                    “Authorized User” means any employee, contractor, or agent
                    authorized by the Client to use the Service.
                  </p>

                  <p>
                    “Client Data” means all electronic data submitted to the
                    Service by or on behalf of the Client, including employee
                    records, attendance data, payroll information, and statutory
                    details.
                  </p>

                  <p>
                    “Sensitive Personal Data” means personal data classified as
                    sensitive under applicable Indian law, including financial
                    information, biometric data, and salary details.
                  </p>

                  <p>
                    “Service Data” means anonymized and aggregated data derived
                    from use of the Service, excluding Client Data.
                  </p>

                  <p>
                    “Subscription Plan” means the pricing tier selected by the
                    Client.
                  </p>

                  <p>
                    “DPDP Act” means the Digital Personal Data Protection Act,
                    2023 and rules framed thereunder.
                  </p>

                  <p>
                    “Force Majeure Event” means events beyond reasonable control
                    including natural disasters, government actions, power
                    failures, internet outages, or cyber incidents not caused by
                    gross negligence.
                  </p>
                </div>

                <h3 className="font-semibold mb-2">
                  3. GRANT OF LICENSE AND ACCESS
                </h3>

                <div className="space-y-4 mb-3 text-gray-700 dark:text-gray-300">
                  {/* 3.1 */}
                  <div>
                    <h4 className="font-semibold mb-1">3.1 License</h4>

                    <p className="leading-relaxed">
                      Subject to compliance with these Terms and payment of
                      applicable fees, Element Tree grants a limited,
                      non-exclusive, non-transferable, revocable license to
                      access and use the Service solely for internal business
                      purposes.
                    </p>
                  </div>

                  {/* 3.2 */}
                  <div>
                    <h4 className="font-semibold mb-1">3.2 Restrictions</h4>

                    <p className="leading-relaxed">You shall not:</p>

                    <ul className="list-disc ml-6 space-y-1">
                      <li>License, sell, lease, or sublicense the Service</li>
                      <li>
                        Reverse engineer, decompile, or disassemble the Platform
                      </li>
                      <li>
                        Access the Service to build or benchmark a competing
                        product
                      </li>
                      <li>Circumvent security or access controls</li>
                    </ul>
                  </div>

                  {/* 3.3 */}
                  <div>
                    <h4 className="font-semibold mb-1">3.3 Usage Limits</h4>

                    <p className="leading-relaxed">
                      You shall not use the Service in a manner that exceeds
                      reasonable usage limits, disrupts performance, or
                      compromises security, including automated scraping or bulk
                      extraction without written consent.
                    </p>
                  </div>
                </div>

                <h3 className="font-semibold mb-2">
                  4. PAYROLL AND COMPLIANCE DISCLAIMER (CRITICAL)
                </h3>

                <div className="space-y-4 text-gray-700 dark:text-gray-300">
                  {/* 4.1 */}
                  <div>
                    <h4 className="font-semibold mb-1">
                      4.1 Calculation Aid Only
                    </h4>

                    <p className="leading-relaxed">
                      The Service is a software tool that assists with payroll
                      calculations based on Client-provided data.
                    </p>
                  </div>

                  {/* 4.2 */}
                  <div>
                    <h4 className="font-semibold mb-1">
                      4.2 Client Responsibility
                    </h4>

                    <p className="leading-relaxed">
                      You are solely responsible for:
                    </p>

                    <ul className="list-disc ml-6 space-y-1">
                      <li>Accuracy of all inputs</li>
                      <li>
                        Verification of payroll outputs before disbursement
                      </li>
                      <li>
                        Compliance with applicable laws including the Income Tax
                        Act, EPF Act, ESIC Act, and state labor laws
                      </li>
                    </ul>
                  </div>

                  {/* 4.3 */}
                  <div>
                    <h4 className="font-semibold mb-1">
                      4.3 No Professional Advice
                    </h4>

                    <p className="leading-relaxed">
                      Element Tree is not a Chartered Accountant, tax advisor,
                      or law firm. Use of the Service does not constitute
                      professional advice. Element Tree does not file statutory
                      returns or remit payments unless expressly agreed in
                      writing under a separate agreement.
                    </p>
                  </div>

                  {/* 4.4 */}
                  <div>
                    <h4 className="font-semibold mb-1">
                      4.4 No Reliance for Audits
                    </h4>

                    <p className="leading-relaxed">
                      The Service and any reports, registers, computations, or
                      outputs generated therein are provided for internal
                      operational convenience only. The Service is not designed,
                      intended, or certified to be relied upon as a system of
                      record for statutory, tax, labor, financial, or compliance
                      audits.
                    </p>

                    <p className="leading-relaxed">
                      You acknowledge that government authorities, auditors, or
                      regulators may require independent verification,
                      reconciliation, or additional documentation beyond what is
                      generated by the Service. Element Tree shall have no
                      liability arising from any audit observations,
                      qualifications, or adverse findings.
                    </p>
                  </div>
                </div>

                <div className="space-y-6 mt-3 text-gray-700 dark:text-gray-300">
                  {/* 5 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      5. CLIENT DATA AND PRIVACY
                    </h3>

                    <h4 className="font-semibold mb-1">5.1 Ownership</h4>
                    <p className="mb-3">
                      Client retains all rights to Client Data. Element Tree
                      processes data solely to provide the Service.
                    </p>

                    <h4 className="font-semibold mb-1">5.2 Data Integrity</h4>
                    <p className="mb-3">
                      Client is responsible for legality, accuracy, and consent
                      related to Client Data.
                    </p>

                    <h4 className="font-semibold mb-1">5.3 Processing</h4>
                    <p className="mb-3">
                      Data processing is governed by the Privacy Policy (Part
                      2).
                    </p>

                    <h4 className="font-semibold mb-1">
                      5.4 Data Protection Roles
                    </h4>
                    <p className="mb-3">
                      Client acts as Data Fiduciary and Element Tree acts as
                      Data Processor under applicable data protection laws.
                    </p>

                    <h4 className="font-semibold mb-1">
                      5.5 Client Controls and Access Management
                    </h4>
                    <p className="mb-3">
                      The Client is solely responsible for configuring and
                      managing user access rights, approval workflows,
                      segregation of duties, and internal controls within the
                      Service. Element Tree shall not be responsible for losses
                      or damages arising from misconfiguration, excessive
                      privileges, or unauthorized actions by Authorized Users.
                    </p>
                  </div>

                  {/* 6 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      6. SERVICE AVAILABILITY, MAINTENANCE, AND DISCONTINUATION
                    </h3>

                    <h4 className="font-semibold mb-1">
                      6.1 Availability Disclaimer
                    </h4>
                    <p className="mb-3">
                      The Service is provided on an “as is” and “as available”
                      basis. Element Tree does not warrant that the Service will
                      meet all regulatory, audit, or enterprise control
                      requirements, nor that operation will be uninterrupted,
                      timely, secure, or error-free.
                    </p>

                    <h4 className="font-semibold mb-1">
                      6.2 Maintenance and Updates
                    </h4>
                    <p className="mb-3">
                      Element Tree may update, modify, suspend, or enhance the
                      Service at any time for maintenance, security, or
                      functional improvements.
                    </p>

                    <h4 className="font-semibold mb-1">
                      6.3 SERVICE SUSPENSION OR DISCONTINUATION
                    </h4>

                    <p className="mb-2">
                      Element Tree reserves the right, at its sole discretion,
                      to suspend or permanently discontinue the Service (in
                      whole or in part) at any time for business, technical,
                      legal, operational, or financial reasons, including where
                      the Service becomes commercially unviable.
                    </p>

                    <p className="mb-2">
                      Where reasonably practicable, Element Tree will provide at
                      least thirty (30) days’ prior notice of such suspension or
                      discontinuation. In cases involving security risks, legal
                      obligations, force majeure events, or circumstances beyond
                      reasonable control, suspension or termination may occur
                      immediately without prior notice.
                    </p>

                    <p className="mb-3">
                      In the event of permanent discontinuation, Subscribers
                      will be provided a reasonable opportunity to export their
                      Client Data in accordance with Section 11. No refunds
                      shall be payable for any prepaid or unused subscription
                      fees, except where such refund is expressly required under
                      applicable law.
                    </p>
                  </div>

                  {/* 7 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      7. FEES, INVOICING, AND TAXES
                    </h3>

                    <h4 className="mb-2">
                      7.1 Fees are billed in advance on a monthly or annual
                      basis.
                    </h4>
                    <h4 className="mb-2">
                      7.2 All fees are exclusive of applicable taxes including
                      GST.
                    </h4>
                    <h4 className="mb-2">
                      7.3 Late payments may incur interest up to 1.5% per month
                      or the maximum permitted by law.
                    </h4>
                    <h4 className="mb-3">
                      7.4 Fees once paid are non-refundable, except where
                      explicitly required by applicable law.
                    </h4>
                  </div>

                  {/* 8 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      8. CONFIDENTIALITY
                    </h3>
                    <p className="mb-3">
                      Each party shall protect the other’s Confidential
                      Information. Confidentiality obligations survive
                      termination for five (5) years.
                    </p>
                  </div>

                  {/* 9 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      9. INDEMNIFICATION
                    </h3>
                    <p className="mb-3">
                      You agree to indemnify and hold Element Tree harmless from
                      claims arising out of misuse of the Service, violation of
                      these Terms, inaccurate or incomplete Client Data, or
                      disputes between you and your employees.
                    </p>
                  </div>

                  {/* 10 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      10. LIMITATION OF LIABILITY
                    </h3>

                    <p className="mb-3">
                      10.1 Element Tree shall not be liable for any indirect,
                      incidental, special, punitive, or consequential damages.
                    </p>

                    <p className="mb-3">
                      10.2 Element Tree’s total aggregate liability shall not
                      exceed the total fees paid by you in the twelve (12)
                      months preceding the claim.
                    </p>
                  </div>

                  {/* 11 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      11. TERMINATION
                    </h3>

                    <p className="mb-3">
                      11.1 You may cancel your subscription at any time through
                      the Platform.
                    </p>

                    <p className="mb-3">
                      11.2 Element Tree may terminate or suspend access
                      immediately for breach of these Terms.
                    </p>

                    <p className="mb-3">
                      11.3 Upon termination, you will have thirty (30) days to
                      export your Client Data. After this period, Client Data
                      may be permanently deleted. Backup copies may persist
                      temporarily as part of routine system backups but will not
                      be restored or used.
                    </p>
                  </div>

                  {/* 12 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      12. GOVERNING LAW AND JURISDICTION
                    </h3>

                    <p>
                      These Terms shall be governed by the laws of India. Courts
                      located in Mumbai, Maharashtra shall have exclusive
                      jurisdiction.
                    </p>
                  </div>

                  {/* 13 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      13. NO RELIANCE; INDEPENDENT VERIFICATION
                    </h3>

                    <p className="mb-3">
                      The Client acknowledges and agrees that:
                    </p>

                    <p className="mb-3">
                      (a) Zenith HRMS is an operational automation tool and not
                      a substitute for internal controls, professional judgment,
                      or statutory compliance processes;
                    </p>

                    <p className="mb-3">
                      (b) The Client shall independently review, validate, and
                      approve all payroll, tax, statutory, and compliance
                      outputs before use or submission to any authority;
                    </p>

                    <p className="mb-3">
                      (c) No content, report, computation, dashboard, or output
                      generated by the Service shall be relied upon as
                      professional advice or as the sole basis for regulatory
                      filings, certifications, or audit representations.
                    </p>

                    <p className="mb-3">
                      Element Tree expressly disclaims any responsibility for
                      audit outcomes, statutory assessments, penalties,
                      interest, or compliance findings arising from reliance on
                      the Service outputs.
                    </p>
                  </div>

                  {/* 14 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      14. FORCE MAJEURE
                    </h3>

                    <p>
                      Neither party shall be liable for failure or delay in
                      performance of its obligations under these Terms (other
                      than payment obligations) due to a Force Majeure Event.
                      Performance shall resume as soon as reasonably practicable
                      after the Force Majeure Event ceases.
                    </p>
                  </div>

                  {/* 15 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white">
                      15. DISCLAIMER OF WARRANTIES
                    </h3>

                    <p>
                      The Service is provided on an “as is” and “as available”
                      basis. To the maximum extent permitted by law, Element
                      Tree disclaims all warranties, whether express, implied,
                      statutory, or otherwise, including any implied warranties
                      of merchantability, fitness for a particular purpose,
                      accuracy, reliability, or non-infringement.
                    </p>
                  </div>

                  {/* 16 */}
                  <div>
                    <h3 className="font-semibold mb-2 dark:text-white   ">
                      16. MISCELLANEOUS
                    </h3>

                    <h4 className="font-semibold mb-1">
                      16.1 Entire Agreement
                    </h4>
                    <p className="mb-3">
                      These Terms constitute the entire agreement between the
                      parties and supersede all prior understandings.
                    </p>

                    <h4 className="font-semibold mb-1">16.2 Severability</h4>
                    <p className="mb-3">
                      If any provision is held invalid or unenforceable, the
                      remaining provisions shall remain in full force and
                      effect.
                    </p>

                    <h4 className="font-semibold mb-1">16.3 Waiver</h4>
                    <p className="mb-3">
                      Failure to enforce any provision shall not constitute a
                      waiver of that provision.
                    </p>
                  </div>
                </div>
              </section>
            </CardContent>

            <CardFooter className="p-6 flex items-center justify-end border-t border-gray-200 dark:border-gray-800">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Back
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>

        {/* PRIVACY POLICY TAB (placeholder – we fill later) */}
        <TabsContent value="privacy" className="mt-4">
          <Card className="rounded-lg shadow-md">
            <CardHeader className="p-6 border-b border-gray-200 dark:border-gray-800">
              <CardTitle className="text-lg font-semibold">
                Privacy Policy
              </CardTitle>

              <CardDescription className="mt-1 text-sm">
                How we collect, use, and protect personal data on the Zenith
                HRMS & Payroll platform.
              </CardDescription>
            </CardHeader>

            <CardContent className="p-6 text-sm leading-relaxed max-h-[60vh] overflow-y-auto space-y-6">
              {/* SECTION 1 */}
              <section>
                <h3 className="font-semibold mb-2">1. INTRODUCTION</h3>

                <p className="text-gray-700 dark:text-gray-300">
                  This Privacy Policy explains how Element Tree processes
                  personal data through the Zenith platform in compliance with
                  the Information Technology Act, 2000, the SPDI Rules, and the
                  Digital Personal Data Protection Act, 2023.
                </p>
              </section>

              {/* SECTION 2 */}
              <section>
                <h3 className="font-semibold mb-2">
                  2. INFORMATION WE COLLECT
                </h3>

                <div className="space-y-2 text-gray-700 dark:text-gray-300">
                  <p>
                    <strong>Subscriber Data:</strong> Organization and
                    authorized user details.
                  </p>
                  <p>
                    <strong>Employee Data:</strong> Personal details, financial
                    information, statutory identifiers.
                  </p>
                  <p>
                    <strong>Biometric Data:</strong> Processed only as encrypted
                    hashes; raw biometric images or templates are never stored.
                  </p>
                  <p>
                    <strong>Usage Data:</strong> Logs, IP addresses, device and
                    browser metadata.
                  </p>
                </div>
              </section>

              {/* SECTION 3 */}
              <section>
                <h3 className="font-semibold mb-2">3. PURPOSE OF PROCESSING</h3>

                <ul className="list-disc ml-6 space-y-1 text-gray-700 dark:text-gray-300">
                  <li>Payroll calculation and HR operations</li>
                  <li>Statutory reporting and compliance</li>
                  <li>Authentication, security, and fraud prevention</li>
                  <li>System notifications and service communications</li>
                </ul>
              </section>

              {/* SECTION 4 */}
              <section>
                <h3 className="font-semibold mb-2">4. DATA SHARING</h3>

                <p className="mb-2 text-gray-700 dark:text-gray-300">
                  We do not sell personal data. Data may be shared with:
                </p>

                <ul className="list-disc ml-6 space-y-1 text-gray-700 dark:text-gray-300">
                  <li>
                    Authorized service providers under confidentiality
                    obligations
                  </li>
                  <li>Government authorities when legally required</li>
                  <li>Successor entities in case of merger or acquisition</li>
                </ul>

                <p className="mt-2 text-gray-700 dark:text-gray-300">
                  Some processing may occur outside India in compliance with
                  applicable laws.
                </p>
              </section>

              {/* SECTION 5 */}
              <section>
                <h3 className="font-semibold mb-2">5. DATA SECURITY</h3>

                <p className="text-gray-700 dark:text-gray-300">
                  Element Tree implements reasonable and appropriate technical
                  and organizational security measures aligned with generally
                  accepted information security standards (including ISO/IEC
                  27001 principles), taking into account the nature of the data
                  processed, risks involved, and industry practices.
                </p>
              </section>

              {/* SECTION 6 */}
              <section>
                <h3 className="font-semibold mb-2">6. DATA RETENTION</h3>

                <div className="space-y-1 text-gray-700 dark:text-gray-300">
                  <p>
                    <strong>Active accounts:</strong> Data retained during
                    subscription.
                  </p>
                  <p>
                    <strong>Cancelled accounts:</strong> Data retained for up to
                    60 days.
                  </p>
                  <p>
                    <strong>Statutory records:</strong> Certain financial logs
                    retained up to 8 years as required by law.
                  </p>
                </div>
              </section>

              {/* SECTION 7 */}
              <section>
                <h3 className="font-semibold mb-2">7. USER RIGHTS</h3>

                <p className="text-gray-700 dark:text-gray-300">
                  Employees should contact their employer for data requests.
                  Subscribers may access, correct, or export data through the
                  Platform, subject to applicable law.
                </p>
              </section>

              {/* SECTION 8 */}
              <section>
                <h3 className="font-semibold mb-2">8. COOKIES</h3>

                <p className="text-gray-700 dark:text-gray-300">
                  Only essential session and security cookies are used.
                </p>
              </section>

              {/* SECTION 9 */}
              <section>
                <h3 className="font-semibold mb-2">9. GRIEVANCE REDRESSAL</h3>

                <div className="space-y-1 text-gray-700 dark:text-gray-300">
                  <p>
                    <strong>Grievance Officer:</strong>
                  </p>
                  <p>Name: Mr. Nikhil Jathar</p>
                  <p>Email: privacy@elementree.co.in</p>
                </div>

                <p className="mt-2 text-gray-700 dark:text-gray-300">
                  Grievances will be acknowledged within 24 hours and addressed
                  within 15 business days.
                </p>
              </section>

              {/* SECTION 10 */}
              <section>
                <h3 className="font-semibold mb-2">10. POLICY UPDATES</h3>

                <p className="text-gray-700 dark:text-gray-300">
                  Continued use of the Service after updates constitutes
                  acceptance of the revised policy.
                </p>
              </section>
            </CardContent>

            <CardFooter className="p-6 flex items-center justify-end border-t border-gray-200 dark:border-gray-800">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Back
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PrivacyPolicy;
